import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiDownload, FiPlus, FiCheck, FiEdit, FiArchive,
  FiAlertTriangle, FiSend, FiCreditCard, FiFileText,
  FiDollarSign, FiSmartphone, FiLayers, FiShield
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { leaseAPI, pdfAPI } from '../../services/api';
import Modal from '../../components/shared/Modal';

// Configurations des méthodes de règlement
const PAYMENT_METHODS = [
  { id: 'cash', name: '💵 Espèces / Caisse Agence', icon: '💵' },
  { id: 'orange_money', name: '📱 Orange Money Mali', icon: '📱' },
  { id: 'moov_money', name: '📱 Moov Money Mali', icon: '📱' },
  { id: 'wave', name: '🌊 Wave Mali', icon: '🌊' },
  { id: 'bank_transfer', name: '🏦 Virement Bancaire', icon: '🏦' },
  { id: 'check', name: '📜 Chèque Bancaire', icon: '📜' },
  { id: 'card', name: '💳 Carte Bancaire', icon: '💳' },
];

const LeaseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // États principaux
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);

  // État de la modale de paiement
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMode, setPaymentMode] = useState('complete'); // 'complete' | 'tranche' | 'advance'
  const [selectedLeasePaymentId, setSelectedLeasePaymentId] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method_id: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    reference: '',
    payer_name: '',
    payer_phone: '',
    notes: '',
    send_sms: true,
    months_count: 1,
  });

  // Modal SMS de Relance / Message personnalisé
  const [smsModalPayment, setSmsModalPayment] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);

  // Modal Termes & Conditions du Bail
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsForm, setTermsForm] = useState('');
  const [termsSaving, setTermsSaving] = useState(false);

  // Chargement des données du contrat
  const fetchContract = useCallback(async () => {
    try {
      const res = await leaseAPI.getById(id);
      setContract(res.data);
    } catch (err) {
      console.error('Erreur chargement contrat:', err);
      toast.error('Impossible de charger le contrat');
      navigate('/leases');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const handleSaveTerms = async (e) => {
    e.preventDefault();
    setTermsSaving(true);
    try {
      await leaseAPI.update(contract.id, { terms: termsForm });
      setContract(prev => ({ ...prev, terms: termsForm }));
      setShowTermsModal(false);
      toast.success('Termes et clauses du bail mis à jour avec succès');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la mise à jour des termes du bail');
    } finally {
      setTermsSaving(false);
    }
  };

  // Liste des échéances filtrées & triées chronologiquement
  const sortedPayments = useMemo(() => {
    if (!contract?.payments) return [];
    return [...contract.payments].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  }, [contract]);

  // Échéances impayées ou partielles éligibles au paiement
  const unpaidPayments = useMemo(() => {
    return sortedPayments.filter((p) => {
      const currentPaid = parseFloat(p.effective_paid_amount !== undefined ? p.effective_paid_amount : (p.paid_amount || 0));
      const totalAmount = parseFloat(p.amount);
      const isPaid = p.status === 'paid' || currentPaid >= totalAmount - 0.01;
      return !isPaid;
    });
  }, [sortedPayments]);

  // Échéance actuellement sélectionnée dans la modale
  const currentSelectedPayment = useMemo(() => {
    return sortedPayments.find((p) => p.id === selectedLeasePaymentId) || null;
  }, [sortedPayments, selectedLeasePaymentId]);

  // Calcul du solde restant pour l'échéance sélectionnée
  const currentSelectedRemaining = useMemo(() => {
    if (!currentSelectedPayment) return 0;
    const currentPaid = parseFloat(currentSelectedPayment.effective_paid_amount !== undefined ? currentSelectedPayment.effective_paid_amount : (currentSelectedPayment.paid_amount || 0));
    const totalAmount = parseFloat(currentSelectedPayment.amount);
    return Math.max(0, totalAmount - currentPaid);
  }, [currentSelectedPayment]);

  // Indicateurs financiers du contrat
  const financialStats = useMemo(() => {
    if (!sortedPayments.length) {
      const rent = parseFloat(contract?.monthly_rent || 0);
      return { totalDue: rent, totalPaid: 0, totalRemaining: rent, paidCount: 0, totalCount: 0, recoveryRate: 0 };
    }

    let totalDue = 0;
    let totalPaid = 0;
    let paidCount = 0;

    sortedPayments.forEach((p) => {
      const amt = parseFloat(p.amount) || 0;
      const pd = parseFloat(p.effective_paid_amount !== undefined ? p.effective_paid_amount : (p.paid_amount || 0));
      totalDue += amt;
      totalPaid += Math.min(pd, amt);
      if (p.status === 'paid' || pd >= amt - 0.01) {
        paidCount++;
      }
    });

    const totalRemaining = Math.max(0, totalDue - totalPaid);
    const recoveryRate = totalDue > 0 ? Math.round((totalPaid / totalDue) * 100) : 0;

    return {
      totalDue,
      totalPaid,
      totalRemaining,
      paidCount,
      totalCount: sortedPayments.length,
      recoveryRate,
    };
  }, [sortedPayments, contract]);

  // Ouvrir la modale de paiement avec pré-remplissage intelligent
  const handleOpenPaymentModal = (targetPayment = null, defaultMode = 'complete') => {
    setPaymentMode(defaultMode);

    const defaultPayment = targetPayment || unpaidPayments[0] || sortedPayments[0] || null;
    const targetId = defaultPayment ? defaultPayment.id : '';
    setSelectedLeasePaymentId(targetId);

    const monthlyRent = parseFloat(contract?.monthly_rent || defaultPayment?.amount || 0);
    const remaining = defaultPayment
      ? Math.max(0, parseFloat(defaultPayment.amount) - parseFloat(defaultPayment.effective_paid_amount !== undefined ? defaultPayment.effective_paid_amount : (defaultPayment.paid_amount || 0)))
      : monthlyRent;

    setPaymentForm({
      amount: defaultMode === 'advance' ? String(monthlyRent * 2) : String(remaining > 0 ? remaining : monthlyRent),
      payment_method_id: 'cash',
      payment_date: new Date().toISOString().split('T')[0],
      reference: '',
      payer_name: `${contract?.tenant_first_name || ''} ${contract?.tenant_last_name || ''}`.trim(),
      payer_phone: contract?.tenant_phone || '',
      notes: '',
      send_sms: true,
      months_count: defaultMode === 'advance' ? 2 : 1,
    });

    setShowPaymentModal(true);
  };

  // Changement de l'échéance sélectionnée dans la modale
  const handleSelectPayment = (paymentId) => {
    setSelectedLeasePaymentId(paymentId);
    const target = sortedPayments.find((p) => p.id === paymentId);
    if (target) {
      const currentPaid = parseFloat(target.effective_paid_amount !== undefined ? target.effective_paid_amount : (target.paid_amount || 0));
      const remaining = Math.max(0, parseFloat(target.amount) - currentPaid);
      if (paymentMode === 'complete') {
        setPaymentForm((prev) => ({ ...prev, amount: String(remaining) }));
      } else if (paymentMode === 'tranche') {
        setPaymentForm((prev) => {
          const num = parseFloat(prev.amount);
          const isValid = !isNaN(num) && num > 0 && num <= remaining;
          return {
            ...prev,
            amount: isValid ? prev.amount : String(Math.round(remaining / 2) || remaining)
          };
        });
      }
    }
  };

  // Changement du mode de paiement (Complete / Tranche / Avance)
  const handleChangeMode = (mode) => {
    setPaymentMode(mode);
    const monthlyRent = parseFloat(contract?.monthly_rent || 0);

    if (mode === 'complete') {
      const remaining = currentSelectedRemaining > 0 ? currentSelectedRemaining : monthlyRent;
      setPaymentForm((prev) => ({ ...prev, amount: String(remaining) }));
    } else if (mode === 'tranche') {
      const remaining = currentSelectedRemaining > 0 ? currentSelectedRemaining : monthlyRent;
      setPaymentForm((prev) => ({ ...prev, amount: String(Math.round(remaining / 2) || remaining) }));
    } else if (mode === 'advance') {
      const count = paymentForm.months_count || 2;
      setPaymentForm((prev) => ({ ...prev, amount: String(monthlyRent * count) }));
    }
  };

  // Soumission du paiement
  const handleSavePayment = async (e) => {
    e.preventDefault();

    const numericAmount = parseFloat(paymentForm.amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      toast.error('Veuillez saisir un montant de paiement valide supérieur à 0 FCFA.');
      return;
    }

    // Validation mode tranche
    if (paymentMode === 'tranche') {
      if (!selectedLeasePaymentId) {
        toast.error('Veuillez sélectionner l\'échéance à régler.');
        return;
      }
      if (numericAmount > currentSelectedRemaining + 0.01) {
        toast.error(`Le montant saisi (${numericAmount.toLocaleString('fr-FR')} FCFA) dépasse le reste à payer (${currentSelectedRemaining.toLocaleString('fr-FR')} FCFA).`);
        return;
      }
    }

    // Validation mode complete
    if (paymentMode === 'complete') {
      if (!selectedLeasePaymentId) {
        toast.error('Veuillez sélectionner l\'échéance à régler.');
        return;
      }
      if (currentSelectedRemaining <= 0) {
        toast.error('Cette échéance est déjà intégralement payée.');
        return;
      }
    }

    setActionLoading(true);
    try {
      const payload = {
        payment_mode: paymentMode,
        lease_payment_id: paymentMode !== 'advance' ? selectedLeasePaymentId : undefined,
        amount: numericAmount,
        payment_method_id: paymentForm.payment_method_id,
        payment_date: paymentForm.payment_date,
        reference: paymentForm.reference.trim() || undefined,
        payer_name: paymentForm.payer_name.trim() || undefined,
        payer_phone: paymentForm.payer_phone.trim() || undefined,
        notes: paymentForm.notes.trim() || undefined,
        send_sms: paymentForm.send_sms,
        months_count: paymentMode === 'advance' ? parseInt(paymentForm.months_count, 10) : undefined,
      };

      const res = await leaseAPI.addPayment(id, payload);
      toast.success(res.data?.message || 'Paiement de loyer enregistré avec succès !');
      setShowPaymentModal(false);
      await fetchContract();
    } catch (err) {
      console.error('Erreur enregistrement paiement:', err);
      toast.error(err.response?.data?.error || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setActionLoading(false);
    }
  };

  // Téléchargement quittance de loyer PDF
  const handleDownloadReceipt = async (paymentId, e) => {
    e.stopPropagation();
    setDownloadingReceiptId(paymentId);
    try {
      const res = await leaseAPI.downloadRentReceipt(paymentId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Quittance_Loyer_${contract.contract_number}_${paymentId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Quittance PDF téléchargée avec succès');
    } catch (err) {
      console.error('Erreur téléchargement quittance:', err);
      toast.error('Erreur lors du téléchargement de la quittance');
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  // Envoi relance SMS
  const handleOpenSmsModal = (payment) => {
    setSmsModalPayment(payment);
    const dueDateFmt = new Date(payment.due_date).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    const remaining = Math.max(0, parseFloat(payment.amount) - parseFloat(payment.effective_paid_amount !== undefined ? payment.effective_paid_amount : (payment.paid_amount || 0)));
    setSmsMessage(
      `Bonjour ${contract.tenant_first_name} ${contract.tenant_last_name}, rappel concernant le loyer de ${dueDateFmt} (Reste dû: ${remaining.toLocaleString('fr-FR')} FCFA) pour votre contrat ${contract.contract_number}. Merci de procéder au règlement.`
    );
  };

  const handleSendSmsRelance = async (e) => {
    e.preventDefault();
    if (!smsModalPayment) return;
    setSendingSms(true);
    try {
      await leaseAPI.sendPaymentSMS(smsModalPayment.id, {
        message: smsMessage,
        phone: contract.tenant_phone,
        name: `${contract.tenant_first_name} ${contract.tenant_last_name}`,
      });
      toast.success('Relance SMS envoyée au locataire avec succès');
      setSmsModalPayment(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'envoi du SMS');
    } finally {
      setSendingSms(false);
    }
  };

  // Actions de cycle de vie du contrat
  const downloadPDF = async () => {
    try {
      const { data } = await pdfAPI.downloadContract(id);
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrat-${contract.contract_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Contrat PDF téléchargé');
    } catch (err) {
      toast.error('Erreur téléchargement contrat PDF');
    }
  };

  const handleValidate = async () => {
    if (!window.confirm('Valider ce contrat de bail ?')) return;
    try {
      const { data } = await leaseAPI.validate(id);
      setContract((prev) => ({ ...prev, ...data }));
      toast.success('Contrat validé');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur');
    }
  };

  const handleSign = async () => {
    if (!window.confirm('Marquer ce contrat comme signé ?')) return;
    try {
      const { data } = await leaseAPI.sign(id);
      setContract((prev) => ({ ...prev, ...data }));
      toast.success('Contrat marqué comme signé');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur');
    }
  };

  const handleActivate = async () => {
    if (!window.confirm('Activer le contrat et générer les échéances mensuelles ?')) return;
    try {
      await leaseAPI.activate(id);
      toast.success('Contrat activé et échéances générées avec succès');
      await fetchContract();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur');
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Archiver ce contrat ?')) return;
    try {
      const { data } = await leaseAPI.archive(id);
      setContract((prev) => ({ ...prev, ...data }));
      toast.success('Contrat archivé');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur');
    }
  };

  const handleTerminate = async () => {
    const reason = window.prompt('Motif de résiliation du bail :');
    if (!reason) return;
    try {
      await leaseAPI.terminate(id, { termination_reason: reason });
      toast.success('Contrat résilié');
      await fetchContract();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur');
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!contract) return null;

  return (
    <div className="page-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 16px' }}>
      {/* ── EN-TÊTE DE LA PAGE ─────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 20,
          background: '#ffffff',
          padding: '16px 20px',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => navigate('/leases')}
            title="Retour à la liste des contrats"
          >
            <FiArrowLeft size={16} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#0f172a' }}>
                Contrat {contract.contract_number}
              </h1>
              <span
                className={`badge badge-${contract.status === 'active' ? 'success' : contract.status === 'terminated' ? 'danger' : 'warning'}`}
                style={{ fontSize: 12, padding: '4px 10px', textTransform: 'uppercase' }}
              >
                {contract.status}
              </span>
            </div>
            <p style={{ margin: '3px 0 0 0', fontSize: 13, color: '#64748b' }}>
              Locataire : <strong>{contract.tenant_first_name} {contract.tenant_last_name}</strong> | Bien : <strong>{contract.property_title}</strong>
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={downloadPDF}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
          >
            <FiDownload /> Contrat PDF
          </button>

          {contract.status === 'active' && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleOpenPaymentModal(null, 'complete')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, padding: '8px 16px' }}
            >
              <FiDollarSign size={18} /> Enregistrer un Paiement
            </button>
          )}
        </div>
      </div>

      {/* ── CYCLE DE VIE DU CONTRAT ────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {[
              { key: 'draft', label: 'Brouillon' },
              { key: 'pending_signature', label: 'Validation' },
              { key: 'pending_activation', label: 'Signature' },
              { key: 'active', label: 'Actif' },
              { key: 'terminated', label: 'Résilié' },
              { key: 'archived', label: 'Archivé' },
            ].map((step, i, arr) => {
              const statuses = ['draft', 'pending_signature', 'pending_activation', 'active', 'terminated', 'archived'];
              const currentIdx = statuses.indexOf(contract.status);
              const stepIdx = statuses.indexOf(step.key);
              const isDone = stepIdx < currentIdx;
              const isCurrent = step.key === contract.status;
              return (
                <React.Fragment key={step.key}>
                  <div
                    style={{
                      padding: '5px 14px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                      background: isCurrent ? 'var(--primary, #2563eb)' : isDone ? '#16a34a' : '#f1f5f9',
                      color: isCurrent || isDone ? '#ffffff' : '#64748b',
                      border: '1px solid ' + (isCurrent ? 'var(--primary, #2563eb)' : isDone ? '#16a34a' : '#e2e8f0'),
                    }}
                  >
                    {step.label}
                  </div>
                  {i < arr.length - 1 && <span style={{ color: '#cbd5e1' }}>→</span>}
                </React.Fragment>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            {contract.status === 'draft' && (
              <button className="btn btn-primary btn-sm" onClick={handleValidate}><FiCheck /> Valider le contrat</button>
            )}
            {contract.status === 'pending_signature' && (
              <button className="btn btn-primary btn-sm" onClick={handleSign}><FiEdit /> Marquer comme signé</button>
            )}
            {contract.status === 'pending_activation' && (
              <button className="btn btn-success btn-sm" onClick={handleActivate}><FiCheck /> Activer + Générer échéances</button>
            )}
            {contract.status === 'active' && (
              <button className="btn btn-danger btn-sm" onClick={handleTerminate}><FiAlertTriangle /> Résilier le bail</button>
            )}
            {['terminated', 'active'].includes(contract.status) && (
              <button className="btn btn-secondary btn-sm" onClick={handleArchive}><FiArchive /> Archiver</button>
            )}
          </div>
        </div>
      </div>

      {/* ── GRILLE DETAILS CONTRAT & CAUTION ───────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 20 }}>
        {/* Détails du bail */}
        <div className="card">
          <div className="card-header">
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiFileText color="#2563eb" /> Détails du Bail
            </h2>
          </div>
          <div className="card-body">
            <div className="detail-row"><div className="detail-label">Bien loué</div><div className="detail-value">{contract.property_title}</div></div>
            <div className="detail-row"><div className="detail-label">Adresse</div><div className="detail-value">{contract.property_address || '-'} ({contract.property_city || 'Bamako'})</div></div>
            <div className="detail-row"><div className="detail-label">Locataire</div><div className="detail-value"><strong>{contract.tenant_first_name} {contract.tenant_last_name}</strong></div></div>
            <div className="detail-row"><div className="detail-label">Téléphone</div><div className="detail-value">{contract.tenant_phone || '-'}</div></div>
            <div className="detail-row"><div className="detail-label">Loyer mensuel</div><div className="detail-value" style={{ fontWeight: 800, color: '#15803d', fontSize: 15 }}>{parseInt(contract.monthly_rent).toLocaleString('fr-FR')} FCFA / mois</div></div>
            <div className="detail-row">
              <div className="detail-label">Frais d'agence</div>
              <div className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong style={{ color: '#0f172a' }}>
                  {parseInt(contract.agency_fee || contract.charges || 0).toLocaleString('fr-FR')} FCFA
                </strong>
                {parseFloat(contract.agency_fee || contract.charges || 0) > 0 && (
                  contract.agency_fee_recorded ? (
                    <span className="badge badge-success" style={{ fontSize: 10, padding: '2px 6px' }}>
                      ✓ Commission comptabilisée
                    </span>
                  ) : (
                    <span className="badge badge-info" style={{ fontSize: 10, padding: '2px 6px' }}>
                      À l'activation du bail
                    </span>
                  )
                )}
              </div>
            </div>
            <div className="detail-row"><div className="detail-label">Date début bail</div><div className="detail-value">{new Date(contract.start_date).toLocaleDateString('fr-FR')}</div></div>
            <div className="detail-row"><div className="detail-label">Date fin bail</div><div className="detail-value">{contract.end_date ? new Date(contract.end_date).toLocaleDateString('fr-FR') : 'Indéterminée (tacite reconduction)'}</div></div>
          </div>
        </div>

        {/* Caution & Dépôt */}
        <div className="card">
          <div className="card-header">
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiShield color="#7c3aed" /> Caution & Dépôt de Garantie
            </h2>
          </div>
          <div className="card-body">
            <div className="detail-row">
              <div className="detail-label">Caution prévue</div>
              <div className="detail-value" style={{ fontWeight: 800 }}>{parseInt(contract.deposit_amount || 0).toLocaleString('fr-FR')} FCFA</div>
            </div>
            {contract.deposits?.length > 0 ? (
              contract.deposits.map((d) => (
                <div key={d.id} style={{ background: '#f8fafc', padding: 12, borderRadius: 8, marginTop: 10, border: '1px solid #e2e8f0' }}>
                  <div className="detail-row"><div className="detail-label">Montant enregistré</div><div className="detail-value" style={{ fontWeight: 700 }}>{parseInt(d.amount).toLocaleString('fr-FR')} FCFA</div></div>
                  <div className="detail-row">
                    <div className="detail-label">Statut caution</div>
                    <div className="detail-value">
                      <span className={`badge badge-${d.status === 'paid' ? 'success' : 'warning'}`}>{d.status}</span>
                    </div>
                  </div>
                  {d.paid_date && (
                    <div className="detail-row"><div className="detail-label">Payée le</div><div className="detail-value">{new Date(d.paid_date).toLocaleDateString('fr-FR')}</div></div>
                  )}
                </div>
              ))
            ) : (
              <p style={{ color: '#64748b', fontSize: 13, marginTop: 10 }}>Aucun dépôt de garantie enregistré pour ce contrat.</p>
            )}
          </div>
        </div>
      </div>

      {/* ── SECTION TERMES & CONDITIONS PARTICULIÈRES DU BAIL ───────────── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a' }}>
            <FiFileText color="#2563eb" /> Termes, Clauses & Conditions Particulières du Bail
          </h2>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => {
              setTermsForm(contract.terms || '');
              setShowTermsModal(true);
            }}
            style={{ fontSize: 12, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FiEdit size={13} /> Modifier les termes
          </button>
        </div>
        <div className="card-body" style={{ padding: '16px 20px' }}>
          {contract.terms ? (
            <p style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 13, lineHeight: '1.6', color: '#1e293b' }}>
              {contract.terms}
            </p>
          ) : (
            <p style={{ color: '#64748b', margin: 0, fontSize: 13, fontStyle: 'italic' }}>
              Aucune clause ou condition particulière n'a été saisie pour ce contrat. Cliquez sur « Modifier les termes » pour en ajouter.
            </p>
          )}
        </div>
      </div>

      {/* ── SECTION FINANCIÈRE & PAIEMENTS DE LOYER ───────────────────── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a' }}>
              <FiCreditCard color="#2563eb" /> Échéancier & Encaissements de Loyer ({sortedPayments.length})
            </h2>
            <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#64748b' }}>
              Suivi des loyers échus, paiements complets, acomptes par tranche et avances.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => handleOpenPaymentModal(null, 'advance')}
              title="Enregistrer un paiement de plusieurs mois d'avance"
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
            >
              <FiLayers /> Paiement d'Avance (N mois)
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => handleOpenPaymentModal(null, 'complete')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}
            >
              <FiPlus /> Nouveau Paiement
            </button>
          </div>
        </div>

        {/* Indicateurs financiers */}
        <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div style={{ background: '#ffffff', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Total Loyers Bail</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>{financialStats.totalDue.toLocaleString('fr-FR')} FCFA</div>
            </div>

            <div style={{ background: '#ffffff', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase' }}>Total Encaissé</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#15803d', marginTop: 2 }}>{financialStats.totalPaid.toLocaleString('fr-FR')} FCFA</div>
              <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>{financialStats.recoveryRate}% du contrat</div>
            </div>

            <div style={{ background: '#ffffff', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase' }}>Reste à Percevoir</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#b45309', marginTop: 2 }}>{financialStats.totalRemaining.toLocaleString('fr-FR')} FCFA</div>
            </div>

            <div style={{ background: '#ffffff', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Échéances Réglées</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>{financialStats.paidCount} / {financialStats.totalCount}</div>
            </div>
          </div>
        </div>

        {/* Tableau des échéances (Responsive) */}
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }}>
          <table style={{ width: '100%', minWidth: 850, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569', fontWeight: 700 }}>
                <th style={{ padding: '12px 16px', width: 140 }}>Échéance</th>
                <th style={{ padding: '12px 16px', width: 120 }}>Loyer Dû</th>
                <th style={{ padding: '12px 16px', width: 130 }}>Cumul Payé</th>
                <th style={{ padding: '12px 16px', width: 130 }}>Reste à Payer</th>
                <th style={{ padding: '12px 16px', width: 110 }}>Date Paiement</th>
                <th style={{ padding: '12px 16px' }}>Mode & Réf</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', width: 120 }}>Statut</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedPayments.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                    Aucune échéance générée. Veuillez activer le contrat pour générer le calendrier de paiement.
                  </td>
                </tr>
              ) : (
                sortedPayments.map((p) => {
                  const currentPaid = parseFloat(p.effective_paid_amount !== undefined ? p.effective_paid_amount : (p.paid_amount || 0));
                  const totalAmount = parseFloat(p.amount);
                  const remaining = Math.max(0, totalAmount - currentPaid);
                  const isFullyPaid = p.status === 'paid' || currentPaid >= totalAmount - 0.01;
                  const isPartial = p.status === 'partially_paid' || (currentPaid > 0 && currentPaid < totalAmount - 0.01);
                  const dueDateObj = new Date(p.due_date);
                  const monthLabel = dueDateObj.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

                  return (
                    <tr
                      key={p.id}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        background: isFullyPaid ? '#f0fdf410' : isPartial ? '#fffbeb10' : 'transparent',
                        transition: 'background 0.15s',
                      }}
                    >
                      {/* Échéance */}
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#1e293b' }}>
                        <div>{dueDateObj.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                        <div style={{ fontSize: 11, color: '#64748b', textTransform: 'capitalize' }}>{monthLabel}</div>
                      </td>

                      {/* Loyer Dû */}
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a' }}>
                        {totalAmount.toLocaleString('fr-FR')} FCFA
                      </td>

                      {/* Cumul Payé */}
                      <td style={{ padding: '12px 16px', color: currentPaid > 0 ? '#15803d' : '#94a3b8', fontWeight: currentPaid > 0 ? 700 : 500 }}>
                        {currentPaid.toLocaleString('fr-FR')} FCFA
                      </td>

                      {/* Reste à Payer */}
                      <td style={{ padding: '12px 16px' }}>
                        {remaining > 0 ? (
                          <span style={{ fontWeight: 800, color: '#b45309' }}>
                            {remaining.toLocaleString('fr-FR')} FCFA
                          </span>
                        ) : (
                          <span style={{ color: '#16a34a', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <FiCheck size={14} /> 0 FCFA
                          </span>
                        )}
                      </td>

                      {/* Date Paiement */}
                      <td style={{ padding: '12px 16px', color: '#475569', fontSize: 12 }}>
                        {p.paid_date ? new Date(p.paid_date).toLocaleDateString('fr-FR') : '—'}
                      </td>

                      {/* Mode & Réf */}
                      <td style={{ padding: '12px 16px', color: '#475569', fontSize: 12 }}>
                        <div>{p.payment_method_name || '—'}</div>
                        {p.reference && (
                          <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>
                            Réf: {p.reference}
                          </div>
                        )}
                      </td>

                      {/* Statut */}
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {isFullyPaid ? (
                          <span className="badge badge-success" style={{ fontWeight: 700, fontSize: 11, padding: '4px 8px' }}>
                            ✅ Payé
                          </span>
                        ) : isPartial ? (
                          <span className="badge badge-warning" style={{ fontWeight: 700, fontSize: 11, padding: '4px 8px', background: '#fef3c7', color: '#b45309' }}>
                            ⏳ Partiel ({Math.round((currentPaid / totalAmount) * 100)}%)
                          </span>
                        ) : (
                          <span className="badge badge-secondary" style={{ fontWeight: 600, fontSize: 11, padding: '4px 8px' }}>
                            📅 En attente
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          {/* Bouton Régler si non payé */}
                          {!isFullyPaid && (
                            <button
                              type="button"
                              className="btn btn-success btn-sm"
                              onClick={() => handleOpenPaymentModal(p, isPartial ? 'tranche' : 'complete')}
                              title={`Régler cette échéance (Reste: ${remaining.toLocaleString('fr-FR')} FCFA)`}
                              style={{ padding: '4px 10px', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                              <FiCreditCard size={13} /> {isPartial ? 'Solde' : 'Régler'}
                            </button>
                          )}

                          {/* Bouton Quittance si payé ou partiel */}
                          {(isFullyPaid || currentPaid > 0) && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={(e) => handleDownloadReceipt(p.id, e)}
                              disabled={downloadingReceiptId === p.id}
                              title="Télécharger la quittance de loyer PDF"
                              style={{ padding: '4px 8px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                              <FiFileText size={13} color="#2563eb" /> Quittance
                            </button>
                          )}

                          {/* Bouton Relance SMS si impayé */}
                          {!isFullyPaid && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleOpenSmsModal(p)}
                              title="Envoyer un rappel SMS au locataire"
                              style={{ padding: '4px 8px', fontSize: 12 }}
                            >
                              <FiSend size={13} color="#d97706" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MODALE DE PAIEMENT DE LOYER MULTI-MODES (SENIOR WORKFLOW) ─── */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="💳 Enregistrer un Paiement de Loyer"
        maxWidth="680px"
      >
        <form onSubmit={handleSavePayment} style={{ fontSize: 13 }}>
          {/* 1. SÉLECTEUR DE MODE DE PAIEMENT */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 700, marginBottom: 8, display: 'block', color: '#1e293b' }}>
              Type de Règlement
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
              {/* Mode Complet */}
              <div
                onClick={() => handleChangeMode('complete')}
                style={{
                  border: paymentMode === 'complete' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                  background: paymentMode === 'complete' ? '#eff6ff' : '#ffffff',
                  borderRadius: 10,
                  padding: 12,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontWeight: 700, color: paymentMode === 'complete' ? '#1e40af' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                  🎯 Paiement Complet
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  Règlement à 100% de l'échéance sélectionnée
                </div>
              </div>

              {/* Mode Tranche */}
              <div
                onClick={() => handleChangeMode('tranche')}
                style={{
                  border: paymentMode === 'tranche' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                  background: paymentMode === 'tranche' ? '#eff6ff' : '#ffffff',
                  borderRadius: 10,
                  padding: 12,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontWeight: 700, color: paymentMode === 'tranche' ? '#1e40af' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                  🧩 Paiement par Tranche
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  Acompte partiel avec calcul du solde restant
                </div>
              </div>

              {/* Mode Avance */}
              <div
                onClick={() => handleChangeMode('advance')}
                style={{
                  border: paymentMode === 'advance' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                  background: paymentMode === 'advance' ? '#eff6ff' : '#ffffff',
                  borderRadius: 10,
                  padding: 12,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontWeight: 700, color: paymentMode === 'advance' ? '#1e40af' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                  🚀 Paiement par Avance
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  Règlement de plusieurs mois d'avance (N mois)
                </div>
              </div>
            </div>
          </div>

          {/* 2. SÉLECTION ÉCHÉANCE (Si Mode Complet ou Tranche) */}
          {paymentMode !== 'advance' && (
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label style={{ fontWeight: 700, marginBottom: 4, display: 'block' }}>
                Échéance de loyer à régler *
              </label>
              {unpaidPayments.length === 0 ? (
                <div style={{ padding: 12, background: '#f0fdf4', color: '#16a34a', borderRadius: 8, fontWeight: 600 }}>
                  ✅ Toutes les échéances de ce contrat sont déjà intégralement réglées. Pour régler les mois suivants, choisissez le mode <strong>"Paiement par Avance"</strong> ci-dessus.
                </div>
              ) : (
                <select
                  className="form-control"
                  value={selectedLeasePaymentId}
                  onChange={(e) => handleSelectPayment(e.target.value)}
                  required
                  style={{ fontWeight: 600, fontSize: 13 }}
                >
                  {unpaidPayments.map((p) => {
                    const currentPaid = parseFloat(p.effective_paid_amount !== undefined ? p.effective_paid_amount : (p.paid_amount || 0));
                    const remaining = Math.max(0, parseFloat(p.amount) - currentPaid);
                    const monthStr = new Date(p.due_date).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                    return (
                      <option key={p.id} value={p.id}>
                        Loyer {monthStr} — Total: {parseFloat(p.amount).toLocaleString('fr-FR')} FCFA | Reste dû: {remaining.toLocaleString('fr-FR')} FCFA {currentPaid > 0 ? `(Déjà versé: ${currentPaid.toLocaleString('fr-FR')})` : ''}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          )}

          {/* 3. OPTION AVANCE (Nombre de mois) */}
          {paymentMode === 'advance' && (
            <div style={{ marginBottom: 14, background: '#faf5ff', padding: 12, borderRadius: 8, border: '1px solid #e9d5ff' }}>
              <label style={{ fontWeight: 700, marginBottom: 6, display: 'block', color: '#6b21a8' }}>
                Nombre de mois d'avance à régler
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {[1, 2, 3, 6, 12].map((cnt) => (
                  <button
                    key={cnt}
                    type="button"
                    onClick={() => {
                      const monthlyRent = parseFloat(contract?.monthly_rent || 0);
                      setPaymentForm((prev) => ({
                        ...prev,
                        months_count: cnt,
                        amount: String(monthlyRent * cnt),
                      }));
                    }}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 700,
                      border: paymentForm.months_count === cnt ? '2px solid #7c3aed' : '1px solid #cbd5e1',
                      background: paymentForm.months_count === cnt ? '#7c3aed' : '#ffffff',
                      color: paymentForm.months_count === cnt ? '#ffffff' : '#334155',
                      cursor: 'pointer',
                    }}
                  >
                    {cnt} mois ({((parseFloat(contract?.monthly_rent || 0)) * cnt).toLocaleString('fr-FR')} FCFA)
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 4. MONTANT DU PAIEMENT & SIMULATION */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 14 }}>
            <div className="form-group">
              <label style={{ fontWeight: 700, marginBottom: 4, display: 'block' }}>
                Montant à encaisser (FCFA) *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Ex: 50000"
                  min="1"
                  step="1"
                  max={paymentMode === 'tranche' ? currentSelectedRemaining : undefined}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  required
                  style={{ fontWeight: 800, fontSize: 16, color: '#0f172a', paddingRight: 50 }}
                />
                <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontWeight: 700, fontSize: 11, color: '#64748b' }}>
                  FCFA
                </span>
              </div>
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 700, marginBottom: 4, display: 'block' }}>
                Mode de Règlement *
              </label>
              <select
                className="form-control"
                value={paymentForm.payment_method_id}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_method_id: e.target.value })}
                required
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 5. DÉTAILS DE SIMULATION TRANCHE / COMPLET */}
          {paymentMode === 'tranche' && currentSelectedPayment && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <span>Loyer total échéance :</span>
                <strong>{parseFloat(currentSelectedPayment.amount).toLocaleString('fr-FR')} FCFA</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <span>Reste à payer actuel :</span>
                <strong>{currentSelectedRemaining.toLocaleString('fr-FR')} FCFA</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#b45309', fontWeight: 700 }}>
                <span>Nouveau solde restant après acompte :</span>
                <strong>
                  {Math.max(0, currentSelectedRemaining - (parseFloat(paymentForm.amount) || 0)).toLocaleString('fr-FR')} FCFA
                </strong>
              </div>
            </div>
          )}

          {/* 6. DATE DE PAIEMENT & RÉFÉRENCE */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 14 }}>
            <div className="form-group">
              <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                Date de Paiement Effectif *
              </label>
              <input
                type="date"
                className="form-control"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                N° de Reçu / Référence Transaction
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: OM-2026-9812, CHQ-4019, CAISSE-01..."
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
              />
            </div>
          </div>

          {/* 7. COORDONNÉES DU PAYEUR */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 14 }}>
            <div className="form-group">
              <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                Nom du Payeur
              </label>
              <input
                type="text"
                className="form-control"
                value={paymentForm.payer_name}
                onChange={(e) => setPaymentForm({ ...paymentForm, payer_name: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                Téléphone Payeur
              </label>
              <input
                type="text"
                className="form-control"
                value={paymentForm.payer_phone}
                onChange={(e) => setPaymentForm({ ...paymentForm, payer_phone: e.target.value })}
              />
            </div>
          </div>

          {/* 8. OPTION CONFIRMATION SMS BATAKIPLUS */}
          <div style={{ marginBottom: 14, background: '#eff6ff', padding: 10, borderRadius: 8, border: '1px solid #bfdbfe' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={paymentForm.send_sms}
                onChange={(e) => setPaymentForm({ ...paymentForm, send_sms: e.target.checked })}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1e40af' }}>
                <FiSmartphone /> Envoyer un SMS de confirmation / reçu instantané au locataire ({contract.tenant_phone || 'Aucun numéro'})
              </span>
            </label>
          </div>

          {/* 9. NOTES & COMMENTAIRES */}
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
              Notes / Remarques (optionnel)
            </label>
            <textarea
              rows={2}
              className="form-control"
              placeholder="Détails complémentaires, remise de chèque, solde convenu..."
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
            />
          </div>

          {/* BOUTONS D'ACTION */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowPaymentModal(false)}
              disabled={actionLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={actionLoading || (paymentMode !== 'advance' && unpaidPayments.length === 0)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 160, justifyContent: 'center', fontWeight: 700 }}
            >
              {actionLoading ? 'Enregistrement...' : <><FiCheck size={16} /> Valider le Paiement</>}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── MODALE RELANCE SMS MANUELLE ─────────────────────────────────── */}
      {smsModalPayment && (
        <Modal
          isOpen={!!smsModalPayment}
          onClose={() => setSmsModalPayment(null)}
          title={`📱 Envoi Relance SMS : ${contract.tenant_first_name} ${contract.tenant_last_name}`}
          maxWidth="520px"
        >
          <form onSubmit={handleSendSmsRelance} style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Numéro du destinataire</label>
              <input type="text" className="form-control" value={contract.tenant_phone || ''} readOnly style={{ background: '#f8fafc' }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Message SMS</label>
              <textarea
                rows={4}
                className="form-control"
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                required
              />
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                {smsMessage.length} caractères (~{Math.ceil(smsMessage.length / 160)} SMS)
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setSmsModalPayment(null)} disabled={sendingSms}>
                Annuler
              </button>
              <button type="submit" className="btn btn-primary" disabled={sendingSms} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {sendingSms ? 'Envoi...' : <><FiSend size={15} /> Envoyer le SMS</>}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {/* Modal d'édition des termes du bail */}
      <Modal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} title="Modifier les termes & clauses du bail" size="lg">
        <form onSubmit={handleSaveTerms}>
          <div className="form-group">
            <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
              Termes, conditions et clauses particulières du contrat de bail
            </label>
            <textarea
              className="form-control"
              rows={8}
              value={termsForm}
              onChange={e => setTermsForm(e.target.value)}
              placeholder="Exemple: 
- L'appartement est exclusivement destiné à usage d'habitation.
- La sous-location est formellement interdite sans accord écrit du bailleur.
- Le locataire s'engage à entretenir les installations intérieures (plomberie, serrurerie, sanitaires).
- En cas de départ, un préavis de 3 mois doit être signifié par lettre recommandée ou exploit d'huissier..."
              style={{ lineHeight: '1.5', fontFamily: 'inherit' }}
            />
            <small style={{ color: 'var(--text-secondary)', marginTop: 6, display: 'block' }}>
              Ces clauses figureront dans le contrat officiel de bail généré au format PDF et signé par les parties.
            </small>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowTermsModal(false)}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary" disabled={termsSaving}>
              {termsSaving ? 'Enregistrement...' : 'Enregistrer les termes'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default LeaseDetailPage;
